import { tools } from '@worktango/ai-assistant/react';

import { fetchCurrentAccountQuery } from '~/behaviors/react-query/account';
import { surveyQueries } from '~/behaviors/queries/survey.queries';
import { statusIdNames, statusIds, statusNames } from '~/config/settings/survey.settings';
import { SurveyAudienceMode } from '~/interfaces/survey.interface';

export const getSurveysListTool = tools.createTool({
  name: 'get_surveys_list',
  description: `Get a list of surveys and their survey types. This can be used to find surveys that match a specific or partial criteria, like name or date range, or to get a list of all surveys.
  
  Example prompts:
  - 'Get all surveys'
  - 'Get surveys from the last quarter'
  - 'Get surveys with the name "Customer Satisfaction"'
  - 'Get surveys the last 3 sent surveys'
  - 'Get all surveys sent since 2024-01-01'
  `,
  displayName: 'Getting surveys',
  pastTenseDisplayName: 'Fetched surveys list',
  parameters: (t) =>
    t.Object(
      {
        name: t.Optional(
          t.String({ description: 'The name, or part of the name, of the survey to search for', default: '' }),
        ),
        offset: t.Optional(t.Number({ description: 'The number of surveys to skip', default: 0 })),
        limit: t.Optional(t.Number({ description: 'The number of surveys to return', default: 10 })),
        orderBy: t.Optional(
          t.String({
            description:
              'The field to order the surveys by. Here are the possible values to order by: "surveyName", "responseRate", "remindersCount", "totalCount", "completedCount", "created", "startTimeUTC", "endTimeUTC"',
            default: 'created',
          }),
        ),
        isAsc: t.Optional(
          t.Boolean({
            description: 'Whether to order the surveys in ascending or descending order',
            default: true,
          }),
        ),
        startTimeUTC: t.Optional(
          t.String({
            description:
              'The start time of the survey in the format YYYY-MM-DD HH:MM:SS. Start time should always be at the beginning of the day.',
            default: '',
          }),
        ),
        endTimeUTC: t.Optional(
          t.String({
            description:
              'The end time of the survey in the format YYYY-MM-DD HH:MM:SS. End time should always be at the end of the day.',
            default: '',
          }),
        ),
      },
      { required: [] },
    ),
  fulfill: async ({ offset, limit, orderBy, isAsc, name, startTimeUTC, endTimeUTC }) => {
    const { account, company } = await fetchCurrentAccountQuery();

    const { results } = await surveyQueries.fetchSurveyDashboard({
      search: { name, startTimeUTC, endTimeUTC },
      companyId: company?.id,
      languageId: account?.langId,
      offset: offset ?? null,
      limit: limit ?? null,
      orderBy: orderBy ?? null,
      isAsc: isAsc ?? true,
    });

    const formattedResults = results.map(formatSurveyDataToYML).join('\n\n');

    const narrative = `Here are the surveys that match the search criteria:

    Regular survey status:
      Description: The status of the surveys in order of progression.
      Values:
        ${statusNames.CREATED}:
          ID: ${statusIds.CREATED}
        ${statusNames.SCHEDULED}:
          ID: ${statusIds.SCHEDULED}
        ${statusNames.SENDING}:
          ID: ${statusIds.SENDING}
        ${statusNames.IN_PROGRESS}:
          ID: ${statusIds.IN_PROGRESS}
        ${statusNames.COMPLETED}:
          ID: ${statusIds.COMPLETED}

    Migration survey status:
      Description: The status of the surveys in order of progression.
      Values:
        ${statusNames.MIGRATION_PROCESSING}:
          ID: ${statusIds.MIGRATION_PROCESSING}
        ${statusNames.MIGRATION_PENDING_APPROVAL}:
          ID: ${statusIds.MIGRATION_PENDING_APPROVAL}
        ${statusNames.MIGRATION_FAILED}:
          ID: ${statusIds.MIGRATION_FAILED}

    Surveys:
      ${formattedResults}
    `;

    console.log('narrative', narrative);

    return narrative;
  },
});

function formatSurveyDataToYML(survey: any) {
  return `
      Survey "${survey.surveyName}":
        ID: ${survey.id}
        Name: "${survey.surveyName}"
        Status ID: "${survey.statusId}"
        Status name: "${statusIdNames[survey.statusId]}"
        Type ID: ${survey.typeId}
        Survey Style (Standard, Continuous): ${survey.isContinuous ? 'Continuous' : 'Standard'}
        Hash code for survey's preview URL: "${survey.hashCode}"
        Disable email sending: ${survey.disableEmailSending}
        Reminders count: ${survey.remindersCount}

        Time information:
          Timezone name: "${survey.timezoneName}"
          Created at: "${survey.created}"
          Start time UTC: "${survey.startTimeUTC}"
          End time UTC: "${survey.endTimeUTC}"

        Result information:
${formatResultInformation(survey)}
`;
}

function formatResultInformation(survey: any): string {
  if (survey.audienceMode === SurveyAudienceMode.Shared) {
    return `          Audience: Open Survey (anyone with the link can respond; nobody was invited, so it has no response rate)
          Completed responses: ${survey.completedCount}`;
  }
  return `          Invited employees: ${survey.totalCount}
          Completed responses: ${survey.completedCount}
          Response rate: ${survey.responseRate}`;
}
